<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaBusinessAgentAPI from 'dashboard/api/metaBusinessAgent';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ResourceList from './ResourceList.vue';

const props = defineProps({
  hookId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const T = key => t(`INTEGRATION_SETTINGS.META_BUSINESS_AGENT.FAQS.${key}`);

const faqs = ref([]);
const isFetching = ref(false);
const showForm = ref(false);
const question = ref('');
const answer = ref('');
const pendingDelete = ref(null);
const showDeleteModal = ref(false);

const fetchFaqs = async () => {
  isFetching.value = true;
  try {
    const { data } = await MetaBusinessAgentAPI.listFaqs(props.hookId);
    faqs.value = data;
  } catch (error) {
    useAlert(T('API.FETCH_ERROR'));
  } finally {
    isFetching.value = false;
  }
};

onMounted(fetchFaqs);

const resetForm = () => {
  question.value = '';
  answer.value = '';
  showForm.value = false;
};

const createFaq = async () => {
  try {
    await MetaBusinessAgentAPI.createFaq(props.hookId, {
      question: question.value,
      answer: answer.value,
    });
    useAlert(T('API.CREATE_SUCCESS'));
    resetForm();
    fetchFaqs();
  } catch (error) {
    useAlert(T('API.CREATE_ERROR'));
  }
};

const confirmDelete = item => {
  pendingDelete.value = item;
  showDeleteModal.value = true;
};

const deleteFaq = async () => {
  try {
    await MetaBusinessAgentAPI.deleteFaq(props.hookId, pendingDelete.value.id);
    useAlert(T('API.DELETE_SUCCESS'));
    fetchFaqs();
  } catch (error) {
    useAlert(T('API.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4">
    <div class="flex justify-end">
      <Button :label="T('ADD_BUTTON')" @click="showForm = true" />
    </div>
    <div
      v-if="showForm"
      class="flex flex-col gap-3 p-4 rounded-lg bg-n-alpha-1"
    >
      <Input v-model="question" :label="T('QUESTION_LABEL')" />
      <TextArea
        v-model="answer"
        :label="T('ANSWER_LABEL')"
        :max-length="4000"
      />
      <div class="flex justify-end gap-2">
        <Button
          slate
          faded
          :label="$t('INTEGRATION_APPS.ADD.FORM.CANCEL')"
          @click="resetForm"
        />
        <Button
          :label="$t('INTEGRATION_APPS.ADD.FORM.SUBMIT')"
          @click="createFaq"
        />
      </div>
    </div>
    <ResourceList
      :items="faqs"
      :empty-state="T('EMPTY_STATE')"
      @delete="confirmDelete"
    >
      <template #default="{ item }">
        <p class="font-medium text-n-slate-12 m-0">{{ item.question }}</p>
        <p class="text-sm text-n-slate-11 m-0">{{ item.answer }}</p>
      </template>
    </ResourceList>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="deleteFaq"
      :title="T('DELETE_CONFIRM_TITLE')"
      :message="T('DELETE_CONFIRM_MESSAGE')"
      :confirm-text="$t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.INBOX')"
      :reject-text="$t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </div>
</template>
